import { ChangeDetectorRef, Component, Inject, OnInit, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AssetDataPoint, DataPoint } from '@services/datapoints-data.service';
import { IntegrationsService, IWebhookIntegration } from '@services/integrations.service';
import { OrganisationDashboard, OrganisationDataModelNode, OrganisationsHierarchy } from '@services/organisation-data.service';
import { ColorPickerComponent } from '@syncfusion/ej2-angular-inputs';
import { Subject, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';

export class DashboardPanelTemplateInputData {
	accountGUID: string;
	organisationUID: string;
	templates: { text: string, value: number }[];
	templateMetrics: { template: number, metrics: { text: string, value: number }[] }[];
	includeShortcut?: boolean;
	dashboards?: OrganisationDashboard[];
	isHierarchyNode?: boolean;
	node?: any;
	htmlTemplates?: TemplateRef<any>[];
}

@Component({
	selector: 'app-dashboard-panel-template',
	templateUrl: './dashboard-panel-template.component.html',
	styleUrls: ['./dashboard-panel-template.component.scss']
})
export class DashboardPanelTemplateComponent implements OnInit {

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** Dialog Form */
	dialogForm: FormGroup;

	/** templateFields */
	public templateFields: Object = { text: "text", value: "value" };

	/** templateFields */
	public dashboardFields: Object = { text: "DashboardName", value: "OrganisationDashboardID" };

	/** templates */
	public templates: { text: string, value: number }[];

	/** metricNumberOptions */
	public metricNumberOptions: { text: string, value: number }[] = [
		{ text: "One", value: 1 },
		{ text: "Two", value: 2 },
		{ text: "Three", value: 3 },
		{ text: "Four", value: 4 },
		{ text: "Five", value: 5 },
		{ text: "Six", value: 6 }
	];

	/** selectedTemplate */
	public selectedTemplate: number = -1;

	/** selectedNumberOfMetrics */
	public selectedNumberOfMetrics: number = 0;

	/** templateMetrics */
	public templateMetrics: { template: number, metrics: { text: string, value: number }[] }[];

	/** existingDashboards */
	public existingDashboards?: OrganisationDashboard[];

	/** numMetricOptions */
	public numMetricOptions: { text: string, value: number }[] = [];

	/** isHierarchyNode */
	public isHierarchyNode: boolean;

	/** node */
	public node: any;

	/** datapoints */
	public datapoints: DataPoint[] = [];

	/** dpFields */
	public dpFields: Object = { text: "DPTitle", value: "DataPointID" };

	/** selectedDatapoints */
	public selectedDatapoints: DataPoint[] = [];

	/** notValid */
	public notValid: boolean = false;

	/** isMobile */
	public isMobile: boolean = false;

	/** htmlTemplates */
	public htmlTemplates: TemplateRef<any>[];

	/** isTotalising */
	public isTotalising: boolean = false;

	/** webhooks */
	public webhooks: IWebhookIntegration[] = [];

	/** whFields */
	public whFields: Object = { text: "Description", value: "WebhookID" };

	/** selectedWebhooks */
	public selectedWebhooks: IWebhookIntegration[] = [];

	/** colourPickers */
	@ViewChildren("colourPicker") colourPickers!: QueryList<ColorPickerComponent>;

	/**
	 * Constructor
	 * @param fb
	 * @param dialogRef
	 * @param data
	 * @param globalFunctions 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<DashboardPanelTemplateComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DashboardPanelTemplateInputData,
		public globalFunctions: GlobalFunctions,
		private assetsDataService: AssetsDataService,
		private integrationsService: IntegrationsService,
		private cdr: ChangeDetectorRef
	) {
		this.accountGUID = data.accountGUID;
		this.organisationUID = data.organisationUID;
		this.templates = data.includeShortcut ? data.templates : data.templates.filter((x) => x.text != "Shortcut");
		this.templateMetrics = data.templateMetrics;
		this.existingDashboards = data.dashboards;
		this.isHierarchyNode = data.isHierarchyNode ?? false;
		this.node = data.node;
		this.htmlTemplates = data.htmlTemplates ?? [];

		this.isMobile = this.checkIfMobile();

		if (this.isHierarchyNode && this.node) {
			this.getDatapointsForHierarchyNode();
		}

		if (!this.isHierarchyNode && this.node) {
			let flattenedNodes = this.flattenNodes([this.node], true);
			flattenedNodes.forEach((x: OrganisationDataModelNode) => this.datapoints.push(...x.Datapoints ?? []));
			if (this.node.DataPointID) {
				this.datapoints.push(this.node);
			}
		}

		this.dialogForm = this.fb.group({
			template: [0, [Validators.required, Validators.min(0)]],
			numberOfMetrics: [0, [Validators.required]],
			metric1: [''],
			metric2: [''],
			metric3: [''],
			metric4: [''],
			metric5: [''],
			metric6: [''],
			datapoints: [[]],
			range1Min: [[]],
			range1Max: [[]],
			range2Min: [[]],
			range2Max: [[]],
			range3Min: [[]],
			range3Max: [[]],
			range1Colour: [[]],
			range2Colour: [[]],
			range3Colour: [[]],
			range1Border: [[]],
			range2Border: [[]],
			range3Border: [[]],
			range1Name: [[]],
			range2Name: [[]],
			range3Name: [[]],
			totalise: [false],
			webhooks: [[]],
			seriesColours: [[]]
		});

		this.selectedTemplate = 0;
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.dialogForm.get("template")?.valueChanges.subscribe(value => {
			this.templateChange(value);
		});
		this.tabSelected({ selectedIndex: 0 });
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * getNumberOfMetricsForTemplate
	 * @param templateID templateID 
	 * @returns 
	 */
	getNumberOfMetricsForTemplate(templateID: number): { text: string, value: number }[] {
		let result: number = 0;
		let findMetrics = this.templateMetrics.find((x) => x.template == templateID);
		if (findMetrics) {
			result = findMetrics.metrics.length;
			if (templateID == 2) {
				result = 1;
			}
		}
		return this.metricNumberOptions.slice(0, result);
	}

	/**
	 * setMetricNumberOptions
	 * @param event event 
	 */
	setMetricNumberOptions(event: any) {
		this.numMetricOptions = this.getNumberOfMetricsForTemplate(event.value);

		if (this.numMetricOptions.length == 1) {
			this.selectedNumberOfMetrics = 1;
			this.dialogForm.get("numberOfMetrics")?.setValue(this.selectedNumberOfMetrics);
			this.dialogForm.get("metric1")?.setValue(0);
		} else {
			this.selectedNumberOfMetrics = 0;
			this.dialogForm.get("numberOfMetrics")?.setValue(this.selectedNumberOfMetrics);
			this.dialogForm.get("metric1")?.setValue("");
		}
	}

	/**
	 * getDatapointsForHierarchyNode
	 */
	async getDatapointsForHierarchyNode() {
		let datapoints: DataPoint[] = [];
		const assets = this.getAssetsFromHierarchyTreeNode(this.node);
		for (let i: number = 0; i < assets.length; i++) {
			const assetDPs: AssetDataPoint[] = await this.getAssetDataPoints(assets[i]);
			datapoints.push(...assetDPs);
		}
		this.datapoints = datapoints;
	}

	/**
	 * getAssetsFromHierarchyTreeNode
	 * @param treeNode treeNode 
	 */
	getAssetsFromHierarchyTreeNode(treeNode: OrganisationsHierarchy): LevelAsset[] {
		let result: LevelAsset[] = [];
		const flattenedLocations = this.flattenSubLocations([treeNode], true);
		flattenedLocations.forEach((x) => {
			if (this.isParsableAsNumber(x.Uid)) {
				result.push(x)
			}
		})
		return result;
	}

	/**
	 * Check if string can be parsed as int
	 * @param str
	 * @returns
	 */
	isParsableAsNumber(str: any): boolean {
		const parsed = Number(str);
		return !isNaN(parsed);
	}

	/**
	 * 	getAssetDataPoints
	 * @param asset asset 
	 */
	getAssetDataPoints(asset: LevelAsset): Promise<AssetDataPoint[]> {
		return new Promise<AssetDataPoint[]>((resolve, reject) => {
			const fetchDataSubject = new Subject<void>();

			this.assetsDataService.getAssetDataPoints$(this.accountGUID, this.organisationUID, asset.AssetID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((datapoints) => !!datapoints),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (datapoints) => {
					resolve(datapoints);
				},
				error: (error) => {
					reject();
				},
			});
		});
	}

	/**
	 * flattenSubLocations
	 * @param hierarchy hierarchy 
	 * @returns 
	 */
	flattenSubLocations(hierarchy: OrganisationsHierarchy[], includeTopLevel: boolean = false): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		if (includeTopLevel) {
			result.push(...hierarchy);
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenSubLocations(item.SubChild));
		}

		return result;
	}

	/**
	 * datapointChoiceChange
	 * @param event event 
	 */
	datapointChoiceChange(event: any) {
		this.selectedDatapoints = event.value.map((x: number) => this.datapoints.find((y: DataPoint) => y.DataPointID == x));
		this.notValid = false;
		// not allowed: 0, 1 when totalising from data model, 2 when data model, 6 when data model
		if (!this.isHierarchyNode && ([0, 2, 6].includes(this.selectedTemplate) || this.selectedTemplate == 1 && this.isTotalising)) {
			this.notValid = new Set(this.selectedDatapoints.map((x: DataPoint) => x.SI)).size > 1;
		}
		// if (!this.isHierarchyNode || ![2, 10].includes(this.selectedTemplate)) {
		// 	for (let i: number = 0; i < this.selectedDatapoints.length; i++) {
		// 		if (this.selectedDatapoints[i].SI != this.selectedDatapoints[0].SI) {
		// 			this.notValid = true;
		// 		}
		// 	}
		// }
	}

	/**
	 * onDialogClose
	 * @param event event 
	 */
	onDialogClose() {
		// if (this.selectedTemplate == 11 && this.selectedWebhooks.length == 0) {
		// 	this.selectedWebhooks = this.webhooks;
		// }
		this.dialogRef.close({ ...this.dialogForm.value, datapoints: this.selectedDatapoints, webhooks: this.selectedWebhooks });
	}

	/**
	 * templateChange
	 * @param value event 
	 */
	templateChange(value: any) {
		if (this.isHierarchyNode) {
			this.selectedDatapoints = [];
			this.dialogForm.get("datapoints")?.setValue([]);
			if ([0, 1, 2, 3].includes(value)) {
				this.dialogForm.get("datapoints")?.setValidators(Validators.required);
			} else {
				this.dialogForm.get("datapoints")?.clearValidators();
			}
			this.dialogForm.get("datapoints")?.updateValueAndValidity();
		}
	}

	/**
	 * oneSIValidator
	 * @param control control  
	 */
	oneSIValidator(datapoints: DataPoint[]): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			let result: boolean = true;
			const value = control.value;
			let chosenSI: string | undefined = undefined;
			for (let i: number = 0; i < value.length; i++) {
				const dpID: number = value[i];
				const datapoint = datapoints.find((x: DataPoint) => x.DataPointID == dpID);
				if (datapoint) {
					if (!chosenSI) {
						chosenSI = datapoint.SI;
					} else {
						if (datapoint.SI != chosenSI) {
							result = false;
						}
					}
				}
			}
			return result ? { oneSI: true } : null;
		}
	}

	/**
	 * rangeChange
	 * @param event 
	 */
	rangeChange(event: any, range: number, isMin: boolean, dpIndex: number) {
		const key: string = `range${range}${isMin ? 'Min' : 'Max'}`;
		let control: AbstractControl | null = this.dialogForm.get(key);
		if (control) {
			let value = control.value;
			value[dpIndex] = event.value;
			control.setValue(value);
		}
	}

	/**
	 * colourChange
	 * @param event event  
	 * @param range range 
	 * @param dpIndex dpIndex 
	 */
	colourChange(event: any, range: number, dpIndex: number) {
		const key: string = `range${range}Colour`;
		let control: AbstractControl | null = this.dialogForm.get(key);
		if (control) {
			let value = control.value;
			value[dpIndex] = event.value;
			control.setValue(value);
		}
	}

	/**
	 * borderChange
	 * @param event event 
	 * @param range range 
	 * @param dpIndex dpIndex 
	 */
	borderChange(event: any, range: number, dpIndex: number) {
		const key: string = `range${range}Border`;
		let control: AbstractControl | null = this.dialogForm.get(key);
		if (control) {
			let value = control.value;
			value[dpIndex] = event.checked;
			control.setValue(value);
		}
	}

	/**
	 * rangeNameChange
	 * @param event event 
	 * @param range range 
	 * @param dpIndex dpIndex 
	 */
	rangeNameChange(event: any, range: number, dpIndex: number) {
		const key: string = `range${range}Name`;
		let control: AbstractControl | null = this.dialogForm.get(key);
		if (control) {
			let value = control.value;
			value[dpIndex] = event.itemData.text;
			control.setValue(value);
		}
	}

	/**
	 * checkIfMobile
	 * @returns
	 */
	checkIfMobile(): boolean {
		return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
	}

	/**
	 * flattenNodes
	 * @param nodes nodes  
	 * @returns 
	 */
	flattenNodes(nodes: OrganisationDataModelNode[], includeTopLevelNodes: boolean = false): OrganisationDataModelNode[] {
		let result: OrganisationDataModelNode[] = [];

		// Check if nodes is a valid array before proceeding
		if (!Array.isArray(nodes)) {
			return result;
		}

		if (includeTopLevelNodes) {
			result.push(...nodes);
		}

		// Iterate over each item in the input hierarchy
		for (const item of nodes) {
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
				result = result.concat(this.flattenNodes(item.SubChild));
			}
		}

		return result;
	}

	/**
	 * getTemplateMetrics
	 * @param template template 
	 * @returns 
	 */
	getTemplateMetrics(template: number): any {
		const metrics = this.templateMetrics.find((x) => x.template == template);
		if (metrics) {
			return metrics.metrics;
		}
		return {};
	}

	/**
	 * tabSelected
	 * @param event event  
	 */
	tabSelected(event: any) {
		this.selectedTemplate = this.templates[event.selectedIndex].value;
		this.dialogForm.get("template")?.setValue(this.selectedTemplate);
		this.setMetricNumberOptions({ value: this.selectedTemplate });

		if (this.selectedTemplate == 11 && this.webhooks.length == 0) {
			this.getWebhooks();
		}
	}

	/**
	 * getIconForTemplate
	 * @param template template 
	 */
	getIconForTemplate(template: any): string {
		switch (template.text) {
			case "Text":
				return "text_fields";
			case "Gauge":
			case "Annotated Gauge":
				return "speed";
			case "Chart":
				return "show_chart";
			case "List":
				return "list_alt";
			case "Watch List":
				return "visibility";
			case "Actions":
				return "pending_actions";
			case "Rules":
				return "rule";
			case "Events":
				return "notifications";
			case "Linear Gauge":
				return "straighten";
			case "Webhooks":
				return "webhook";
			case "Pie Chart":
				return "pie_chart";
			default:
				return "help";
		}
	}

	/**
	 * getTemplate
	 * @param template template 
	 */
	getTemplate(template: number): TemplateRef<any> {
		return this.htmlTemplates[template];
	}

	/**
	 * toggleTotalising
	 * @param event event 
	 */
	toggleTotalising(event: any) {
		this.isTotalising = event.checked;
	}

	/** getWebhooks */
	getWebhooks() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.integrationsService.getWebhookIntegrations$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (webhooks: any) => {
				this.webhooks = webhooks;
			},
			error: (error) => {
				if (error.status === 404) {

				}
			}
		});
	}

	/**
	 * webhookChoiceChange
	 * @param event event 
	 */
	webhookChoiceChange(event: any) {
		this.selectedWebhooks = event.value.map((x: number) => this.webhooks.find((y: IWebhookIntegration) => y.WebhookID == x));
	}

	/**
	 * seriesColourChange
	 * @param event event 
	 */
	seriesColourChange(event: any, datapointID?: number) {
		let control: AbstractControl | null = this.dialogForm.get("seriesColours");
		if (control) {
			let value = control.value;
			if (datapointID) {
				let _findValue = value.find((x: any) => x.datapointID == datapointID);
				if (_findValue) {
					_findValue.colour = event.value;
				} else {
					value.push({ datapointID: datapointID, colour: event.value });
				}
			} else {
				value = [{ datapointID: 0, colour: event.value }];
			}
			control.setValue(value);
		}
	}
}
